import React, { useMemo, useState } from "react";
import { Check, Trash2, Plus, Palette, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { BUILTIN_THEMES, FONT_OPTIONS, BUTTON_STYLE_OPTIONS, buildCustomTheme, CURSOR_OPTIONS, CLOCK_OPTIONS } from "./themeEngine";

// ============ Arka plan efektleri (sadece eğlenceli temalar, z-index en altta) ============
export function ThemeBackgroundFX({ theme, enabled }) {
  const stars = useMemo(() => {
    if (theme?.animation !== "stars") return [];
    return Array.from({ length: 45 }, (_, i) => ({
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      delay: `${(Math.random() * 3.5).toFixed(2)}s`,
      size: Math.random() > 0.85 ? 3 : 2,
    }));
  }, [theme?.id]);

  if (!enabled || !theme || theme.animation === "none" || !theme.animation) return null;

  return (
    <div className="zen-theme-fx" data-testid="theme-bg-fx" aria-hidden="true">
      {theme.animation === "blobs" && (
        <>
          <div className="zen-fx-blob zen-fx-blob--1" />
          <div className="zen-fx-blob zen-fx-blob--2" />
          <div className="zen-fx-blob zen-fx-blob--3" />
        </>
      )}
      {theme.animation === "grid" && <div className="zen-fx-grid" />}
      {theme.animation === "stars" && (
        <div className="zen-fx-stars">
          <div className="zen-fx-nebula" />
          {stars.map((s, i) => (
            <div
              key={i}
              className="zen-fx-star"
              style={{ top: s.top, left: s.left, animationDelay: s.delay, width: s.size, height: s.size }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============ Tema kartı ============
function ThemeCard({ theme, active, onSelect, onDelete, tr = (x) => x }) {
  const swatchPrimary = theme.colors?.primary || "#E5322A";
  const swatchAccent = theme.colors?.accent || "#4F46E5";
  const previewBg = theme.mode === "dark" ? (theme.colors?.bgDark || "#0C1220") : (theme.colors?.bgLight || "#F7F7F8");
  const previewPanel = theme.mode === "dark" ? (theme.colors?.panelDark || "#131B2E") : (theme.colors?.panelLight || "#FFFFFF");

  return (
    <div
      data-testid={`theme-card-${theme.id}`}
      onClick={() => onSelect(theme.id)}
      className={`relative zen-panel p-0 overflow-hidden cursor-pointer transition-transform hover:-translate-y-0.5 ${
        active ? "ring-2 ring-[#E5322A]" : ""
      }`}
    >
      {/* Mini preview strip */}
      <div className="h-16 flex items-stretch" style={{ background: previewBg }}>
        <div className="w-1/3 flex flex-col justify-center items-center gap-1" style={{ background: previewPanel, borderRight: `1px solid ${theme.colors?.borderLight || "#00000010"}` }}>
          <span className="w-3 h-3 rounded-full" style={{ background: swatchPrimary }} />
          <span className="w-3 h-3 rounded-full" style={{ background: swatchAccent }} />
        </div>
        <div className="flex-1 flex flex-col justify-center gap-1 px-3">
          <span className="h-1.5 rounded-full w-4/5" style={{ background: swatchPrimary, borderRadius: theme.radius || "6px" }} />
          <span className="h-1.5 rounded-full w-2/3 opacity-40" style={{ background: swatchAccent }} />
        </div>
      </div>
      {active && (
        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#E5322A] flex items-center justify-center">
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        </div>
      )}
      <div className="p-3">
        <div className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
          {tr(theme.name)}
        </div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{tr(theme.desc)}</div>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          <span className="zen-chip bg-slate-100 dark:bg-[#1A2340] text-slate-600 dark:text-slate-300">{theme.buttonStyle || "solid"}</span>
          <span className="zen-chip bg-slate-100 dark:bg-[#1A2340] text-slate-600 dark:text-slate-300">{theme.radius || "12px"}</span>
          {onDelete && (
            <button
              data-testid={`theme-delete-${theme.id}`}
              onClick={(e) => { e.stopPropagation(); onDelete(theme.id); }}
              className="ml-auto p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-500"
              title="Sil"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Özel Tema Oluşturucu Modal ============
function CustomThemeBuilderModal({ onClose, onSave }) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState("light");
  const [primary, setPrimary] = useState("#E5322A");
  const [accent, setAccent] = useState("#4F46E5");
  const [fontId, setFontId] = useState("manrope");
  const [radiusPx, setRadiusPx] = useState(12);
  const [buttonStyle, setButtonStyle] = useState("solid");

  const font = FONT_OPTIONS.find(f => f.id === fontId) || FONT_OPTIONS[0];

  const previewBtnStyle = useMemo(() => {
    const base = { borderRadius: `${Math.round(radiusPx * 0.62)}px`, fontFamily: font.stack };
    if (buttonStyle === "outline") return { ...base, background: "transparent", color: primary, border: `1.5px solid ${primary}` };
    if (buttonStyle === "glass") return { ...base, background: `${primary}CC`, color: "#fff", backdropFilter: "blur(6px)", boxShadow: `0 6px 18px -6px ${primary}88`, border: "none" };
    if (buttonStyle === "clay") return { ...base, background: mode === "dark" ? "#1A2340" : "#E6E9EF", color: primary, boxShadow: "4px 4px 8px rgba(0,0,0,0.12), -4px -4px 8px rgba(255,255,255,0.5)", border: "none" };
    return { ...base, background: primary, color: "#fff", border: "none" };
  }, [buttonStyle, primary, radiusPx, font, mode]);

  const handleSave = () => {
    if (!name.trim()) { toast.error("Lütfen tema için bir ad girin"); return; }
    const theme = buildCustomTheme({ name, mode, primary, accent, fontId, radiusPx, buttonStyle });
    onSave(theme);
  };

  const previewDark = mode === "dark";

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="custom-theme-modal">
      <div className="zen-panel w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-[#22304A] sticky top-0 bg-white dark:bg-[#131B2E] z-10">
          <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100">
            <Palette className="w-4.5 h-4.5 text-[#E5322A]" />
            Özel Tema Oluştur
          </div>
          <button data-testid="custom-theme-close" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-[#1A2340]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Tema Adı</label>
            <input
              data-testid="custom-theme-name-input"
              className="zen-input mt-1"
              placeholder="Örn: Ofis Mavisi"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Mod</label>
              <div className="flex gap-1.5 mt-1">
                {["light", "dark"].map(m => (
                  <button
                    key={m}
                    data-testid={`custom-theme-mode-${m}`}
                    onClick={() => setMode(m)}
                    className={`flex-1 py-2 rounded-md text-xs font-semibold border transition-colors ${
                      mode === m ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300"
                    }`}
                  >
                    {m === "light" ? "Açık" : "Koyu"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Yazı Tipi</label>
              <select
                data-testid="custom-theme-font-select"
                className="zen-input mt-1"
                value={fontId}
                onChange={(e) => setFontId(e.target.value)}
                style={{ fontFamily: font.stack }}
              >
                {FONT_OPTIONS.map(f => <option key={f.id} value={f.id} style={{ fontFamily: f.stack }}>{f.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Ana Renk</label>
              <div className="flex items-center gap-2 mt-1">
                <input data-testid="custom-theme-primary-color" type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} className="w-9 h-9 rounded-md border border-slate-200 dark:border-[#22304A] cursor-pointer bg-transparent" />
                <input data-testid="custom-theme-primary-hex" className="zen-input" value={primary} onChange={(e) => setPrimary(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Vurgu Rengi</label>
              <div className="flex items-center gap-2 mt-1">
                <input data-testid="custom-theme-accent-color" type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="w-9 h-9 rounded-md border border-slate-200 dark:border-[#22304A] cursor-pointer bg-transparent" />
                <input data-testid="custom-theme-accent-hex" className="zen-input" value={accent} onChange={(e) => setAccent(e.target.value)} />
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Köşe Yuvarlaklığı: {radiusPx}px</label>
            <input
              data-testid="custom-theme-radius-slider"
              type="range" min="0" max="32" step="1"
              value={radiusPx}
              onChange={(e) => setRadiusPx(Number(e.target.value))}
              className="w-full mt-2 accent-[#E5322A]"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Buton Stili</label>
            <div className="grid grid-cols-4 gap-1.5 mt-1">
              {BUTTON_STYLE_OPTIONS.map(b => (
                <button
                  key={b.id}
                  data-testid={`custom-theme-btnstyle-${b.id}`}
                  onClick={() => setButtonStyle(b.id)}
                  className={`py-2 rounded-md text-[11px] font-semibold border transition-colors ${
                    buttonStyle === b.id ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          {/* Canlı önizleme */}
          <div>
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">Canlı Önizleme</label>
            <div
              data-testid="custom-theme-live-preview"
              className="p-4 rounded-xl border"
              style={{
                background: previewDark ? "#0C1220" : "#F7F7F8",
                borderColor: previewDark ? "#22304A" : "#E5E5EA",
                fontFamily: font.stack,
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold" style={{ color: previewDark ? "#F0F4FB" : "#0F0F12" }}>Sipariş Özeti</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${accent}22`, color: accent }}>Önizleme</span>
              </div>
              <div className="rounded-lg p-3 mb-3" style={{ background: previewDark ? "#131B2E" : "#FFFFFF", border: `1px solid ${previewDark ? "#22304A" : "#E5E5EA"}` }}>
                <div className="h-2 rounded-full w-1/2 mb-2" style={{ background: previewDark ? "#2B3A58" : "#E5E5EA" }} />
                <div className="grid grid-cols-3 gap-2 text-[11px]" style={{ color: previewDark ? "#8393B5" : "#6B6B75" }}>
                  <span>Ürün</span><span>Adet</span><span>Tutar</span>
                  <span style={{ color: previewDark ? "#F0F4FB" : "#0F0F12" }}>Zebra Perde</span><span style={{ color: previewDark ? "#F0F4FB" : "#0F0F12" }}>2</span><span style={{ color: previewDark ? "#F0F4FB" : "#0F0F12" }}>€120</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button style={previewBtnStyle} className="px-4 py-2 text-xs font-semibold">Onayla</button>
                <button style={{ borderRadius: `${Math.round(radiusPx * 0.62)}px`, border: `1px solid ${previewDark ? "#22304A" : "#E5E5EA"}`, color: previewDark ? "#F0F4FB" : "#0F0F12" }} className="px-4 py-2 text-xs font-semibold">Vazgeç</button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-slate-200 dark:border-[#22304A] sticky bottom-0 bg-white dark:bg-[#131B2E]">
          <button data-testid="custom-theme-cancel-btn" onClick={onClose} className="zen-btn-ghost">Vazgeç</button>
          <button data-testid="custom-theme-save-btn" onClick={handleSave} className="zen-btn-primary">
            <Check className="w-3.5 h-3.5" /> Temayı Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Liquid Glass çift-mod kartı ============
function LiquidGlassCard({ activeId, onSelect }) {
  const isLightActive = activeId === "apple_light";
  const isDarkActive = activeId === "apple_dark";
  const isActive = isLightActive || isDarkActive;

  return (
    <div
      data-testid="theme-card-liquid-glass"
      className={`relative zen-panel p-0 overflow-hidden transition-transform hover:-translate-y-0.5 ${isActive ? "ring-2 ring-[#E5322A]" : ""}`}
    >
      {/* Mini önizleme — sol açık / sağ koyu */}
      <div className="h-16 flex">
        <div className="flex-1 flex flex-col justify-center items-center gap-1"
          style={{ background: "linear-gradient(135deg, #C8DFFF 0%, #A5C8FF 100%)" }}>
          <span className="w-3 h-3 rounded-full" style={{ background: "#007AFF" }} />
          <span className="w-3 h-3 rounded-full" style={{ background: "#5856D6" }} />
        </div>
        <div className="w-px" style={{ background: "rgba(0,0,0,0.08)" }} />
        <div className="flex-1 flex flex-col justify-center items-center gap-1"
          style={{ background: "linear-gradient(135deg, #0A1628 0%, #050810 100%)" }}>
          <span className="w-3 h-3 rounded-full" style={{ background: "#0A84FF" }} />
          <span className="w-3 h-3 rounded-full" style={{ background: "#5E5CE6" }} />
        </div>
      </div>

      {isActive && (
        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#E5322A] flex items-center justify-center">
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        </div>
      )}

      <div className="p-3">
        <div className="text-sm font-bold text-slate-900 dark:text-slate-100">Liquid Glass</div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
          Buzlu cam paneller, sıvı metal butonlar, SF Pro tipografi.
        </div>
        <div className="flex gap-1.5 mt-2.5">
          <button
            data-testid="liquid-glass-light-btn"
            onClick={() => onSelect("apple_light")}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              isLightActive
                ? "bg-[#E5322A] text-white border-[#E5322A]"
                : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#131B2E]"
            }`}
          >
            Açık Mod
          </button>
          <button
            data-testid="liquid-glass-dark-btn"
            onClick={() => onSelect("apple_dark")}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              isDarkActive
                ? "bg-[#E5322A] text-white border-[#E5322A]"
                : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#131B2E]"
            }`}
          >
            Koyu Mod
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Ayarlar > Görünüm sekmesi içeriği ============
export function AppearanceSettings({ zenThemeId, setZenThemeId, customThemes, saveCustomTheme, deleteCustomTheme, animationsEnabled, setAnimationsEnabled, cursorId, setCursorId, clockStyle, setClockStyle, tr = (s) => s }) {
  const [builderOpen, setBuilderOpen] = useState(false);

  const systemThemes = BUILTIN_THEMES.filter(t => t.category === "system");

  const handleSave = (theme) => {
    saveCustomTheme(theme);
    setBuilderOpen(false);
    toast.success(`"${theme.name}" teması oluşturuldu ve uygulandı`);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Bu özel tema silinsin mi?")) return;
    deleteCustomTheme(id);
    toast.success("Özel tema silindi");
  };

  return (
    <div className="space-y-6" data-testid="appearance-settings">
      <div className="zen-panel p-4 flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#E5322A]" /> {tr("Hareketli Efektler")}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{tr("Ana sayfa karşılama panelindeki akışkan arka plan hareketini ve fare ışıltısını aç/kapat.")}</div>
        </div>
        <button
          data-testid="theme-animations-toggle"
          onClick={() => setAnimationsEnabled(!animationsEnabled)}
          className={`relative w-11 h-6 rounded-full transition-colors ${animationsEnabled ? "bg-[#E5322A]" : "bg-slate-300 dark:bg-[#22304A]"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${animationsEnabled ? "translate-x-5" : ""}`} />
        </button>
      </div>

      <div className="zen-panel p-4" data-testid="cursor-picker">
        <div className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-0.5">{tr("Fare İmleci")}</div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">{tr("Uygulama içinde kullanılacak fare imleci stilini seçin. Butonun üzerine gelerek imleci önizleyebilirsiniz.")}</div>
        <div className="flex flex-wrap gap-2">
          {CURSOR_OPTIONS.map(c => (
            <button
              key={c.id}
              data-testid={`cursor-option-${c.id}`}
              onClick={() => setCursorId(c.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors zen-cursor-sample-${c.id} ${
                cursorId === c.id
                  ? "bg-[#E5322A] text-white border-[#E5322A]"
                  : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#131B2E]"
              }`}
            >
              {tr(c.label)}
            </button>
          ))}
        </div>
      </div>

      <div className="zen-panel p-4" data-testid="clock-picker">
        <div className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-0.5">{tr("Ana Sayfa Saati")}</div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">{tr("Ana sayfadaki saatin görünümünü seçin — dijital (Apple yazı tipi) veya Apple analog kadran. Her iki temada da aynı görünüm kullanılır.")}</div>
        <div className="flex flex-wrap gap-2">
          {CLOCK_OPTIONS.map(c => (
            <button
              key={c.id}
              data-testid={`clock-option-${c.id}`}
              onClick={() => setClockStyle(c.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                clockStyle === c.id
                  ? "bg-[#E5322A] text-white border-[#E5322A]"
                  : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#131B2E]"
              }`}
            >
              {tr(c.label)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2.5">{tr("Sistem Temaları")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {systemThemes.map(t => (
            <ThemeCard key={t.id} theme={t} active={zenThemeId === t.id} onSelect={setZenThemeId} tr={tr} />
          ))}
          <LiquidGlassCard activeId={zenThemeId} onSelect={setZenThemeId} />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{tr("Özel Temalarım")}</h3>
          <button data-testid="open-custom-theme-builder-btn" onClick={() => setBuilderOpen(true)} className="zen-btn-primary">
            <Plus className="w-3.5 h-3.5" /> {tr("Yeni Özel Tema")}
          </button>
        </div>
        {customThemes.length === 0 ? (
          <div className="zen-panel p-6 text-center text-sm text-slate-500 dark:text-slate-400">
            {tr("Henüz özel tema oluşturmadınız. Kendi renk, yazı tipi ve buton stilinizle bir tema tasarlayın.")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {customThemes.map(t => (
              <ThemeCard key={t.id} theme={t} active={zenThemeId === t.id} onSelect={setZenThemeId} onDelete={handleDelete} tr={tr} />
            ))}
          </div>
        )}
      </div>

      {builderOpen && <CustomThemeBuilderModal onClose={() => setBuilderOpen(false)} onSave={handleSave} />}
    </div>
  );
}
